import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useMatch } from 'react-router-dom';
import { connect } from 'react-redux';
import { Formik, ErrorMessage, Field, FormikProps } from 'formik';
import * as Yup from 'yup';
import InputBlock from '$/components/global/inputBlock';
import BixButton from '$/components/global/bixButton';
import Loading from '$/components/loading/Loading';
import BixCheckbox from '$/components/global/bixCheckbox';
import NoDataFound from '$/components/global/noData';
import { TranscriptionsAdminService } from '$/services/TranscriptionsAdminService';
import { toastError, toastSuccess } from '$/store/toast/actions';
import * as Bixby from '$/core';
import './index.scss';

interface TranscriptValues {
  relatedText: boolean;
  audioFile: File | undefined;
  audioFileName: string | undefined;
}

interface IUploadTranscriptProps {
  dispatchToastSuccess: (message: string) => void;
  dispatchToastError: (message: string) => void;
}

const UploadTranscript: React.FC<IUploadTranscriptProps> = props => {
  const { dispatchToastSuccess, dispatchToastError } = props;
  const match = useMatch('/admin/transcripts/:transcriptionId/upload');
  const transcriptionId = match?.params.transcriptionId;

  const [isLoading, setIsLoading] = useState(false);
  const [uploadingTranscription, setUploadingTranscription] = useState(false);
  const [uploadError, setUploadError] = useState(false);
  const [uploadSuccessful, setUploadSuccessful] = useState(false);
  const [transcription, setTranscription] = useState(undefined as Bixby.Models.AdminCompanyTranscription | undefined);
  const [validationSchema, setValidationSchema] = useState(undefined as Yup.Schema | undefined);

  useEffect(() => {
    const getTranscription = async () => {
      if (!transcriptionId) return;

      setIsLoading(true);
      try {
        const transcription: Bixby.Models.AdminCompanyTranscription = await TranscriptionsAdminService.getById(
          transcriptionId
        );
        setTranscription(transcription);
      } finally {
        setIsLoading(false);
      }
    };
    getTranscription();
  }, [transcriptionId]);

  useEffect(() => {
    if (transcription) {
      const mnpiRegex = /\*\*\*private mnpi\*\*\*(.*)/i;
      const hasPrivateDeclaration = transcription.hasPrivateDeclaration;

      const bixbyId = hasPrivateDeclaration
        ? transcription.companyBixbyId?.match(mnpiRegex)?.[1]
        : transcription.companyBixbyId;

      setValidationSchema(
        Yup.object({
          relatedText: Yup.boolean().oneOf([true], 'Please verify all files relate to company.'),
          audioFile: Yup.mixed().required('Audio file is required'),
          audioFileName: Yup.string().test(
            'checkAudioFileName',
            hasPrivateDeclaration
              ? `The file must be named Private MNPI - ${bixbyId}.mp3`
              : `The file must be named ${bixbyId}.mp3`,
            value => {
              if (hasPrivateDeclaration) {
                return value?.toLowerCase() === `Private MNPI - ${bixbyId}.mp3`.toLowerCase();
              } else {
                return value?.toLowerCase() === `${transcription.companyBixbyId}.mp3`.toLowerCase();
              }
            }
          ),
        })
      );
    }
  }, [transcription]);

  const upload: (transcriptionId: string, audioFile: Blob) => Promise<void> = async (
    transcriptionId: string,
    audioFile: Blob
  ) => {
    setUploadError(false);
    setUploadSuccessful(false);
    setUploadingTranscription(true);
    try {
      await TranscriptionsAdminService.uploadTranscript(transcriptionId, audioFile);
      dispatchToastSuccess('Transcript uploaded');
      setUploadSuccessful(true);
    } catch (error: any) {
      dispatchToastError('Transcript failed to upload');
      setUploadError(true);
    } finally {
      setUploadingTranscription(false);
    }
  };

  const formikRef = useRef<FormikProps<TranscriptValues>>(null);

  const initialUploadTranscriptValues: TranscriptValues = {
    relatedText: false,
    audioFile: undefined,
    audioFileName: undefined,
  };

  const handleFileUpload = (event: any, setFieldValue: any, field: string, fileType: string | null) => {
    const file: File | undefined = ((event.target as HTMLInputElement).files || [])[0];

    if (file && (fileType === null || file.type === fileType)) {
      setFieldValue(field, file);
      setFieldValue(`${field}Name`, file.name);
    } else {
      setFieldValue(field, null);
      setFieldValue(`${field}Name`, null);
    }
  };

  if (uploadSuccessful) {
    return <Navigate to="/admin/transcripts" />;
  }
  if (isLoading) {
    return <Loading pageLevel />;
  } else if (Bixby.Utils.isNullOrEmpty(transcription)) {
    return (
      <React.Fragment>
        <section>
          <div className="transcript">
            <section className="transcript_upload">
              <NoDataFound dataType="transcription" />
            </section>
          </div>
        </section>
      </React.Fragment>
    );
  } else {
    return (
      <React.Fragment>
        <section>
          <div className="transcript">
            <section className="transcript_upload">
              <Formik
                innerRef={formikRef}
                initialValues={initialUploadTranscriptValues}
                validationSchema={validationSchema}
                onSubmit={async (values: TranscriptValues) => {
                  if (!transcription) return;
                  await upload(transcription?.transcriptionId, values.audioFile as Blob);
                }}
                render={({ handleSubmit, setFieldValue, values }) => (
                  <form onSubmit={handleSubmit} className="profile-input-container">
                    <div className="profile-input-column">
                      <h2>{transcription?.companyName}</h2>
                      <h2>{transcription?.companyBixbyId}</h2>
                      <Field
                        name="audioFile"
                        render={() => {
                          return (
                            <InputBlock title="Audio File">
                              <input
                                id="audioFile"
                                name="audioFile"
                                accept=".mp3, audio/mpeg"
                                className="hidden_file_input"
                                type="file"
                                onChange={e => handleFileUpload(e, setFieldValue, 'audioFile', 'audio/mpeg')}
                                multiple={false}
                              />
                              <label htmlFor="audioFile">
                                {values.audioFile ? values.audioFile.name : 'Choose a .MP3 file'}
                              </label>
                            </InputBlock>
                          );
                        }}
                      />
                      <ErrorMessage name="audioFile">
                        {msg => <div className="error error-message">{msg}</div>}
                      </ErrorMessage>
                      <ErrorMessage name="audioFileName">
                        {msg => <div className="error error-message">{msg}</div>}
                      </ErrorMessage>
                      <div className="acceptance_wrap">
                        <Field
                          name="relatedText"
                          render={({ field }: { [key: string]: any }) => (
                            <BixCheckbox id="relatedText" checked={field.value} onChange={field.onChange(field.name)}>
                              I confer all documents relate to {transcription?.companyName}
                            </BixCheckbox>
                          )}
                        />
                        <ErrorMessage name="relatedText">
                          {msg => <div className="error error-message">{msg}</div>}
                        </ErrorMessage>
                      </div>
                      {uploadingTranscription && <Loading inline />}
                      {uploadError && (
                        <div className="error error-message">Error uploading transcript files. Please try again.</div>
                      )}
                      {!uploadingTranscription && <BixButton type="submit">Upload Transcription Files</BixButton>}
                    </div>
                  </form>
                )}></Formik>
            </section>
          </div>
        </section>
      </React.Fragment>
    );
  }
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchToastSuccess: (message: string) => dispatch(toastSuccess(message)),
  dispatchToastError: (message: string) => dispatch(toastError(message)),
});

export default connect(null, mapDispatchToProps)(UploadTranscript);
